/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Plus, Clock, AtSign, CheckSquare, Square, ListTodo,
  ChevronDown, ChevronUp, Image as ImageIcon, X, User, Paperclip
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
// eslint-disable-next-line no-unused-vars -- FLAGGED (2026-02): photoUrl was
// wired for rendering task attachment thumbnails but the JSX callsite was
// removed. Restore <img src={photoUrl(task.attachment)} /> OR delete this
// import if attachments are intentionally dropped from the task panel.
import { photoUrl } from './slateUtils';
import { AuthImage } from '../AuthImage';
import { ImageLightbox } from '../ImageLightbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Assignee chip
function AssigneeChip({ name, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 bg-[#e8c547]/10 text-[#e8c547] border border-[#e8c547]/20 rounded-full px-2 py-0.5 text-xs">
      <AtSign className="h-3 w-3" />{name}
      {onRemove && (
        <button onClick={onRemove} className="text-[#e8c547]/60 hover:text-red-400 ml-0.5">
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  );
}

// Expanded task detail view
function TaskDetail({ task, token, onUpdate, onClose, projectId, projectUsers }) {
  const [progress, setProgress] = useState(task.progress_notes || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [addAssignee, setAddAssignee] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const imgRef = useRef();
  const headers = { Authorization: `Bearer ${token}` };

  const assignees = task.assignees || (task.assigned_to_name ? [{ user_id: task.assigned_to_user_id, user_name: task.assigned_to_name }] : []);

  const saveProgress = async () => {
    setSaving(true);
    await onUpdate(task.id, { progress_notes: progress });
    setSaving(false);
  };

  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/projects/${projectId}/slate/tasks/${task.id}/images`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      await onUpdate(task.id, {});  // Refresh task
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleAddAssignee = (u) => {
    const current = task.assignees || [];
    if (current.some(a => a.user_id === u.id)) return;
    const newAssignees = [...current, { user_id: u.id, user_name: u.full_name }];
    onUpdate(task.id, { assignees: newAssignees });
    setAddAssignee('');
    setFilteredUsers([]);
  };

  const handleRemoveAssignee = (userId) => {
    const newAssignees = (task.assignees || []).filter(a => a.user_id !== userId);
    onUpdate(task.id, { assignees: newAssignees });
  };

  const images = (task.attachments || []).filter(a => a.type === 'image');

  return (
    <div className="bg-[#111] border border-[#2a2a2a] rounded-xl overflow-hidden" data-testid={`task-detail-${task.id}`}>
      {/* Header */}
      <div className="flex items-start gap-2 p-3 border-b border-[#1a1a1a]">
        <button
          onClick={() => onUpdate(task.id, { status: task.status === 'open' ? 'closed' : 'open' })}
          className="mt-0.5 shrink-0"
        >
          {task.status === 'open'
            ? <Square className="h-4 w-4 text-[#a0a0a0] hover:text-[#e8c547] transition-colors" />
            : <CheckSquare className="h-4 w-4 text-[#e8c547]" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${task.status === 'closed' ? 'line-through text-[#555]' : 'text-[#f0f0f0]'}`}>
            {task.title}
          </div>
          {task.description && (
            <div className="text-xs text-[#888] mt-1 leading-relaxed">{task.description}</div>
          )}
        </div>
        <button onClick={onClose} className="text-[#555] hover:text-[#f0f0f0] shrink-0">
          <ChevronUp className="h-4 w-4" />
        </button>
      </div>

      {/* Assignees */}
      <div className="px-3 py-2 border-b border-[#1a1a1a]">
        <div className="text-[10px] text-[#444] uppercase tracking-wider mb-1.5 flex items-center gap-1">
          <User className="h-3 w-3" /> Assigned To
        </div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {assignees.length > 0 ? (
            assignees.map((a, i) => (
              <AssigneeChip
                key={a.user_id || `assignee-${i}`}
                name={a.user_name || a.user_id}
                onRemove={task.status === 'open' ? () => handleRemoveAssignee(a.user_id) : null}
              />
            ))
          ) : (
            <span className="text-xs text-[#444] italic">Unassigned</span>
          )}
        </div>
        {task.status === 'open' && (
          <div className="relative">
            <Input
              value={addAssignee}
              onChange={e => {
                setAddAssignee(e.target.value);
                setFilteredUsers(
                  e.target.value.trim()
                    ? projectUsers.filter(u =>
                        u.full_name.toLowerCase().includes(e.target.value.toLowerCase()) &&
                        !(task.assignees || []).some(a => a.user_id === u.id)
                      ).slice(0, 5)
                    : []
                );
              }}
              placeholder="Add assignee..."
              className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] text-xs h-7"
              data-testid={`add-assignee-${task.id}`}
            />
            {filteredUsers.length > 0 && (
              <div className="absolute z-10 w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg mt-1 overflow-hidden shadow-xl">
                {filteredUsers.map(u => (
                  <button
                    key={u.id}
                    onClick={() => handleAddAssignee(u)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-[#2a2a2a] text-[#f0f0f0] flex items-center gap-2"
                  >
                    <AtSign className="h-3 w-3 text-[#e8c547]" />
                    {u.full_name}
                    <span className="text-[#555] ml-auto">{u.department?.replace('_', ' ')}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="px-3 py-2 border-b border-[#1a1a1a]">
        <div className="text-[10px] text-[#444] uppercase tracking-wider mb-1.5 flex items-center gap-1">
          <Clock className="h-3 w-3" /> Progress Notes
        </div>
        <Textarea
          value={progress}
          onChange={e => setProgress(e.target.value)}
          placeholder="Add a progress update..."
          rows={2}
          className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] text-xs resize-none min-h-[48px]"
          readOnly={task.status === 'closed'}
          data-testid={`task-progress-${task.id}`}
        />
        {task.status === 'open' && (
          <Button
            size="sm"
            className="bg-[#e8c547] hover:bg-[#d4b340] text-black h-6 text-xs px-3 mt-1.5"
            onClick={saveProgress}
            disabled={saving || progress === task.progress_notes}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {/* Images */}
      <div className="px-3 py-2">
        <div className="text-[10px] text-[#444] uppercase tracking-wider mb-2 flex items-center gap-1">
          <ImageIcon className="h-3 w-3" /> Images
        </div>
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {images.map((img, i) => (
              <AuthImage
                key={i}
                path={img.storage_path}
                alt={img.filename}
                className="h-16 w-16 object-cover rounded-lg cursor-pointer hover:opacity-80 border border-[#2a2a2a]"
                onClick={() => setLightbox(images.map(im => ({ path: im.storage_path, alt: im.filename })))}
              />
            ))}
          </div>
        )}
        {task.status === 'open' && (
          <>
            <input
              ref={imgRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) { handleImageUpload(f); e.target.value = ''; }
              }}
            />
            <button
              onClick={() => imgRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 text-xs text-[#555] hover:text-[#e8c547] transition-colors"
              data-testid={`upload-task-img-${task.id}`}
            >
              <Paperclip className="h-3 w-3" />
              {uploading ? 'Uploading…' : 'Attach image'}
            </button>
          </>
        )}
        {lightbox && (
          <ImageLightbox images={lightbox} initialIndex={0} onClose={() => setLightbox(null)} />
        )}
      </div>
    </div>
  );
}

// Task card (collapsed view)
function TaskCard({ task, token, onUpdate, projectId, projectUsers }) {
  const [expanded, setExpanded] = useState(false);

  const assignees = task.assignees || (task.assigned_to_name ? [{ user_name: task.assigned_to_name }] : []);

  if (expanded) {
    return (
      <TaskDetail
        task={task}
        token={token}
        onUpdate={async (id, updates) => {
          await onUpdate(id, updates);
        }}
        onClose={() => setExpanded(false)}
        projectId={projectId}
        projectUsers={projectUsers}
      />
    );
  }

  return (
    <div
      className={`bg-[#111] border rounded-xl p-3 transition-all cursor-pointer hover:border-[#3a3a3a] ${task.status === 'closed' ? 'border-[#1a1a1a] opacity-60' : 'border-[#2a2a2a]'}`}
      data-testid={`task-${task.id}`}
      onClick={() => setExpanded(true)}
    >
      <div className="flex items-start gap-2">
        <button
          onClick={e => { e.stopPropagation(); onUpdate(task.id, { status: task.status === 'open' ? 'closed' : 'open' }); }}
          className="mt-0.5 shrink-0"
          data-testid={`task-toggle-${task.id}`}
        >
          {task.status === 'open'
            ? <Square className="h-4 w-4 text-[#a0a0a0] hover:text-[#e8c547] transition-colors" />
            : <CheckSquare className="h-4 w-4 text-[#e8c547]" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${task.status === 'closed' ? 'line-through text-[#555]' : 'text-[#f0f0f0]'}`}>
            {task.title}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {assignees.map((a, i) => (
              <span key={a.user_id || `a-${i}`} className="text-[10px] text-[#e8c547]/70 flex items-center gap-0.5">
                <AtSign className="h-2.5 w-2.5" />{a.user_name}
              </span>
            ))}
          </div>
          {task.progress_notes && (
            <div className="mt-1 text-[10px] text-[#666] italic truncate">{task.progress_notes}</div>
          )}
          {(task.attachments || []).filter(a => a.type === 'image').length > 0 && (
            <div className="text-[10px] text-[#555] mt-1 flex items-center gap-1">
              <ImageIcon className="h-2.5 w-2.5" />
              {(task.attachments || []).filter(a => a.type === 'image').length} image(s)
            </div>
          )}
        </div>
        <ChevronDown className="h-3.5 w-3.5 text-[#555] shrink-0 mt-0.5" />
      </div>
    </div>
  );
}

export function TaskPanel({ projectId, channelId, token, projectUsers, prefill }) {
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState('open');
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeInput, setAssigneeInput] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [filteredForAdd, setFilteredForAdd] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTasks(); }, [projectId, channelId]);
  useEffect(() => { if (prefill) { setTitle(prefill); setShowAdd(true); } }, [prefill]);

  const fetchTasks = async () => {
    try {
      const url = channelId
        ? `${API}/projects/${projectId}/slate/tasks?channel_id=${encodeURIComponent(channelId)}`
        : `${API}/projects/${projectId}/slate/tasks`;
      const res = await axios.get(url, { headers });
      setTasks(res.data);
    } catch (err) { console.error('Fetch tasks error:', err); }
  };

  const createTask = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/slate/tasks`, {
        title: title.trim(),
        description,
        channel_id: channelId,
        assignees: selectedAssignees,
        assigned_to_user_id: selectedAssignees[0]?.user_id || null,
        assigned_to_name: selectedAssignees[0]?.user_name || null,
      }, { headers });
      setTitle(''); setDescription(''); setAssigneeInput('');
      setSelectedAssignees([]); setShowAdd(false);
      fetchTasks(); toast.success('Task created');
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const res = await axios.patch(`${API}/projects/${projectId}/slate/tasks/${taskId}`, updates, { headers });
      setTasks(prev => prev.map(t => t.id === taskId ? res.data : t));
    } catch (err) { console.error('Update task error:', err); }
  };

  const open = tasks.filter(t => t.status === 'open');
  const closed = tasks.filter(t => t.status === 'closed');
  const shown = tab === 'open' ? open : closed;

  return (
    <div className="flex flex-col h-full border-l border-[#2a2a2a] bg-[#0d0d0d]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a] shrink-0">
        <div className="flex items-center gap-2">
          <ListTodo className="h-4 w-4 text-[#e8c547]" />
          <span className="font-semibold text-sm text-[#f0f0f0]">Tasks</span>
        </div>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#555] hover:text-[#e8c547]"
          onClick={() => setShowAdd(v => !v)} data-testid="add-task-btn">
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {showAdd && (
        <div className="p-3 border-b border-[#2a2a2a] space-y-2 bg-[#111] shrink-0">
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Task title..." autoFocus
            className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] text-sm h-8" data-testid="task-title-input" />
          <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)"
            className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] text-xs h-7" />

          {/* Multi-assignee */}
          <div>
            {selectedAssignees.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1.5">
                {selectedAssignees.map((a) => (
                  <AssigneeChip key={a.user_id} name={a.user_name}
                    onRemove={() => setSelectedAssignees(prev => prev.filter(x => x.user_id !== a.user_id))} />
                ))}
              </div>
            )}
            <div className="relative">
              <Input
                value={assigneeInput}
                onChange={e => {
                  setAssigneeInput(e.target.value);
                  setFilteredForAdd(
                    e.target.value.trim()
                      ? projectUsers.filter(u =>
                          u.full_name.toLowerCase().includes(e.target.value.toLowerCase()) &&
                          !selectedAssignees.some(a => a.user_id === u.id)
                        ).slice(0, 5)
                      : []
                  );
                }}
                placeholder="Assign to..."
                className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] text-xs h-7"
                data-testid="task-assignee-input"
              />
              {filteredForAdd.length > 0 && (
                <div className="absolute z-10 w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg mt-1 overflow-hidden shadow-xl">
                  {filteredForAdd.map(u => (
                    <button key={u.id}
                      onClick={() => {
                        setSelectedAssignees(prev => [...prev, { user_id: u.id, user_name: u.full_name }]);
                        setAssigneeInput(''); setFilteredForAdd([]);
                      }}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#2a2a2a] text-[#f0f0f0]">
                      @{u.full_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button size="sm" className="bg-[#e8c547] hover:bg-[#d4b340] text-black h-7 text-xs flex-1"
              onClick={createTask} disabled={saving || !title.trim()} data-testid="create-task-submit">
              Create
            </Button>
            <button onClick={() => { setShowAdd(false); setTitle(''); setSelectedAssignees([]); }}
              className="text-xs text-[#555] hover:text-[#f0f0f0]">Cancel</button>
          </div>
        </div>
      )}

      <div className="flex shrink-0 border-b border-[#2a2a2a]">
        {['open', 'closed'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2 text-xs font-medium capitalize transition-colors ${tab === t ? 'text-[#e8c547] border-b-2 border-[#e8c547]' : 'text-[#555] hover:text-[#a0a0a0]'}`}
            data-testid={`task-tab-${t}`}>
            {t} ({t === 'open' ? open.length : closed.length})
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {shown.length === 0 ? (
          <div className="text-center py-8 text-[#444] text-xs">{tab === 'open' ? 'No open tasks' : 'No closed tasks'}</div>
        ) : (
          shown.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              token={token}
              onUpdate={updateTask}
              projectId={projectId}
              projectUsers={projectUsers}
            />
          ))
        )}
      </div>
    </div>
  );
}
